import type { SseMessage } from './sseClient';

/**
 * Cross-tab SSE coordination: exactly ONE tab per browser holds the real SSE
 * connection (the leader); every other tab (followers) mirrors the leader's
 * frames and connection status over a BroadcastChannel.
 *
 * Why: each SSE stream permanently holds one slot of the browser's per-host
 * HTTP/1.1 connection pool (6, shared across tabs). With a connection per
 * tab, extra tabs starve — their dial queues forever and the status chip
 * shows a perpetual "Reconnecting…". One shared connection removes the cap
 * entirely and cuts server fan-out.
 *
 * Election uses the Web Locks API: every participating tab queues an
 * exclusive request for the same lock; the holder is the leader. The browser
 * releases the lock automatically when the holding tab closes or crashes, so
 * succession needs no heartbeats and cannot split-brain. Where Web Locks or
 * BroadcastChannel are unavailable, the coordinator degrades to `solo` —
 * exactly the previous connection-per-tab behavior.
 */

export const SSE_CHANNEL_NAME = 'om-sse';
export const SSE_LEADER_LOCK = 'om-sse-leader';

export type SseCoordMessage =
  /** Leader → followers: a received envelope, plus the leader's replay cursor
   * so a promoted follower can resume the stream without losing events. */
  | { kind: 'frame'; envelope: SseMessage; lastEventId: string | null }
  /** Leader → followers: a connection-status transition. */
  | {
      kind: 'status';
      status: 'connected' | 'reconnecting' | 'off' | 'authError';
      attempt?: number;
      maxRetries?: number;
      message?: string;
    }
  /** Follower → leader: user asked for an immediate reconnect. */
  | { kind: 'reconnect' }
  /** New follower → leader: re-broadcast current status so a late-joining tab
   * doesn't sit in "connecting" until the next transition. */
  | { kind: 'hello' };

export type SseTabRole = 'idle' | 'solo' | 'leader' | 'follower';

export interface SseTabCoordinatorOptions {
  /** This tab must open the real connection (became leader, or solo). */
  onLead: () => void;
  /** Follower-side: apply a broadcast frame/status locally. */
  onRemote: (msg: Extract<SseCoordMessage, { kind: 'frame' | 'status' }>) => void;
  /** Leader-side: a follower asked for an immediate reconnect. */
  onReconnectRequest: () => void;
  /** Leader-side: a new follower joined — re-broadcast current status. */
  onHello: () => void;
}

/** Both primitives are required; degrade to per-tab connections otherwise. */
export function isTabCoordinationSupported(): boolean {
  return (
    typeof BroadcastChannel !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.locks?.request === 'function'
  );
}

export class SseTabCoordinator {
  role: SseTabRole = 'idle';
  /** Replay cursor observed from the leader's frames — adopted when this tab
   * is promoted so Last-Event-ID replay covers the handover gap. */
  lastSeenEventId: string | null = null;

  private channel: BroadcastChannel | null = null;
  private lockAbort: AbortController | null = null;
  /** Resolving this releases the leader lock → the next queued tab leads. */
  private releaseLeadership: (() => void) | null = null;
  private active = false;

  constructor(private readonly opts: SseTabCoordinatorOptions) {}

  start(): void {
    if (this.active) return;
    this.active = true;

    if (!isTabCoordinationSupported()) {
      this.role = 'solo';
      this.opts.onLead();
      return;
    }

    this.channel = new BroadcastChannel(SSE_CHANNEL_NAME);
    this.channel.onmessage = (event: MessageEvent) =>
      this.handleMessage(event.data as SseCoordMessage);
    this.role = 'follower';
    this.channel.postMessage({ kind: 'hello' } satisfies SseCoordMessage);

    this.lockAbort = new AbortController();
    void navigator.locks
      .request(SSE_LEADER_LOCK, { signal: this.lockAbort.signal }, () => {
        // Lock granted — this tab leads until the returned promise resolves
        // (stop()) or the tab dies (browser auto-releases).
        if (!this.active) return undefined;
        this.role = 'leader';
        this.opts.onLead();
        return new Promise<void>((resolve) => {
          this.releaseLeadership = resolve;
        });
      })
      .catch(() => {
        // The request was aborted by stop() while still queued — expected.
      });
  }

  stop(): void {
    if (!this.active) return;
    this.active = false;
    this.lockAbort?.abort();
    this.lockAbort = null;
    this.releaseLeadership?.();
    this.releaseLeadership = null;
    this.channel?.close();
    this.channel = null;
    this.role = 'idle';
  }

  /** Leader-side: mirror a frame/status to every follower. */
  broadcast(msg: Extract<SseCoordMessage, { kind: 'frame' | 'status' }>): void {
    if (this.role !== 'leader') return;
    this.channel?.postMessage(msg);
  }

  /** Follower-side: ask the leader to reconnect immediately. */
  requestReconnect(): void {
    if (this.role !== 'follower') return;
    this.channel?.postMessage({ kind: 'reconnect' } satisfies SseCoordMessage);
  }

  private handleMessage(msg: SseCoordMessage): void {
    if (this.role === 'leader') {
      if (msg.kind === 'reconnect') this.opts.onReconnectRequest();
      else if (msg.kind === 'hello') this.opts.onHello();
      return;
    }
    if (this.role !== 'follower') return;
    if (msg.kind === 'frame') {
      this.lastSeenEventId = msg.lastEventId ?? this.lastSeenEventId;
      this.opts.onRemote(msg);
    } else if (msg.kind === 'status') {
      this.opts.onRemote(msg);
    }
    // Other followers' hello/reconnect messages are the leader's to handle.
  }
}
